/**
 * CRM > Accounts > detail. Account header with the three CRM-owned fields
 * editable inline (owner, tier, status via PATCH /v1/crm/accounts/:id), plus
 * sections for open opportunities, recent orders, and contacts.
 *
 * The Sage-synced columns (name, terms, salesperson) are read-only here; only
 * owner_rep_id / tier / status are user-editable, matching the API.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { Pill } from '@/components/Pill';
import { useSession } from '@/lib/useSession';
import { formatDateShort } from '@/lib/dateUtils';
import {
  accountStatusLabel,
  accountStatusTone,
  formatCrmMoney,
  hasCrmAccounts,
  tierLabel,
  tierTone,
  useCrmAccount,
  useCrmReps,
  usePatchAccount,
  ACCOUNT_STATUS_OPTIONS,
  ACCOUNT_TIER_OPTIONS,
  type AccountStatus,
  type AccountTier,
  type ContactRow,
  type OpportunityRow,
  type RecentOrderRow,
} from '@/lib/useCrm';

export function CrmAccountDetail() {
  const { accountId } = useParams<{ accountId: string }>();
  const [searchParams] = useSearchParams();
  const backSearch = searchParams.toString();
  const { user } = useSession();
  // No more read/write levels: holding crm.accounts grants both.
  const canRead = hasCrmAccounts(user?.permissions, user?.superAdmin);
  const canWrite = canRead;

  const detailQ = useCrmAccount(accountId);
  const backHref = { pathname: '/crm/accounts', search: backSearch ? `?${backSearch}` : '' };

  if (user && !canRead) {
    return (
      <div className="flex h-full min-h-0 flex-col pb-4">
        <Link to={backHref} className="text-sm text-amp-ink-muted hover:text-amp-ink inline-flex items-center gap-1 mb-3 dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink">
          <ArrowLeft className="h-3 w-3" /> Back to accounts
        </Link>
        <p className="text-sm text-amp-ink dark:text-amp-dark-ink">No access.</p>
      </div>
    );
  }

  const data = detailQ.data;

  return (
    <div className="flex h-full min-h-0 flex-col pb-4">
      <Link
        to={backHref}
        className="text-sm text-amp-ink-muted hover:text-amp-ink inline-flex items-center gap-1 mb-3 dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
      >
        <ArrowLeft className="h-3 w-3" /> Back to accounts
      </Link>

      <div className="mb-6">
        <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">CRM / Accounts</p>
        <h1 className="truncate text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">
          {data?.account.name ?? 'Account'}
        </h1>
        {data?.account && (
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted font-mono">
            {data.account.sage_customer_no}
          </p>
        )}
      </div>

      {detailQ.isLoading ? (
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading...</p>
      ) : detailQ.error ? (
        <p className="text-sm text-red-700 dark:text-red-300">Could not load: {detailQ.error.message}</p>
      ) : data ? (
        <div className="min-h-0 flex-1 overflow-auto space-y-5">
          <AccountFieldsPanel
            accountId={Number(accountId)}
            account={data.account}
            canWrite={canWrite}
          />
          <OpportunitiesSection opps={data.open_opportunities} />
          <OrdersSection orders={data.recent_orders} />
          <ContactsSection contacts={data.contacts} />
        </div>
      ) : null}
    </div>
  );
}

function AccountFieldsPanel({
  accountId,
  account,
  canWrite,
}: {
  accountId: number;
  account: {
    tier: AccountTier;
    status: AccountStatus;
    owner_rep_id: number | null;
    terms: string | null;
    brand: string;
    sage_salesperson_no: string | null;
    open_order_amount: string | null;
    weighted_pipeline: string | null;
    open_opp_count: number;
    order_count: number;
    last_order_date: string | null;
  };
  canWrite: boolean;
}) {
  const { reps } = useCrmReps();
  const patch = usePatchAccount();

  const seed = useMemo(
    () => ({
      tier: account.tier,
      status: account.status,
      owner_rep_id: account.owner_rep_id != null ? String(account.owner_rep_id) : '',
    }),
    [account.tier, account.status, account.owner_rep_id],
  );
  const [form, setForm] = useState(seed);
  useEffect(() => setForm(seed), [seed]);

  const dirty =
    form.tier !== seed.tier || form.status !== seed.status || form.owner_rep_id !== seed.owner_rep_id;

  const submit = () => {
    patch.mutate({
      id: accountId,
      body: {
        tier: form.tier,
        status: form.status,
        owner_rep_id: form.owner_rep_id ? Number(form.owner_rep_id) : null,
      },
    });
  };

  const inputCls =
    'w-full border border-amp-line rounded px-2.5 py-1 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink';
  const labelCls = 'block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-0.5';

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {/* CRM-owned editable fields */}
      <div className="border border-amp-line rounded-lg bg-amp-white p-4 dark:border-amp-dark-line dark:bg-amp-dark-surface">
        <h2 className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-3">Account settings</h2>
        {canWrite ? (
          <div className="space-y-3">
            <div>
              <label className={labelCls}>Owner</label>
              <select value={form.owner_rep_id} onChange={(e) => setForm((f) => ({ ...f, owner_rep_id: e.target.value }))} className={inputCls}>
                <option value="">Unassigned</option>
                {reps.map((r) => (
                  <option key={r.id} value={String(r.id)}>{r.name}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Tier</label>
                <select value={form.tier} onChange={(e) => setForm((f) => ({ ...f, tier: e.target.value as AccountTier }))} className={inputCls}>
                  {ACCOUNT_TIER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Status</label>
                <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as AccountStatus }))} className={inputCls}>
                  {ACCOUNT_STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
            {patch.isError && <p className="text-xs text-red-700 dark:text-red-300">{patch.error.message}</p>}
            {patch.isSuccess && !dirty && <p className="text-xs text-green-700 dark:text-green-300">Saved.</p>}
            <button
              type="button"
              onClick={submit}
              disabled={patch.isPending || !dirty}
              className="inline-flex items-center gap-1 text-xs border border-amp-ink bg-amp-ink text-amp-white rounded px-3 py-1.5 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface"
            >
              {patch.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
            </button>
          </div>
        ) : (
          <dl className="grid grid-cols-1 gap-y-1.5 text-sm sm:grid-cols-2">
            <ReadRow label="Owner">{reps.find((r) => r.id === account.owner_rep_id)?.name ?? '--'}</ReadRow>
            <ReadRow label="Tier"><Pill tone={tierTone(account.tier)}>{tierLabel(account.tier)}</Pill></ReadRow>
            <ReadRow label="Status"><Pill tone={accountStatusTone(account.status)}>{accountStatusLabel(account.status)}</Pill></ReadRow>
          </dl>
        )}
      </div>

      {/* Sage-derived read-only facts + rollup stats */}
      <div className="border border-amp-line rounded-lg bg-amp-white p-4 dark:border-amp-dark-line dark:bg-amp-dark-surface">
        <h2 className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-3">Overview</h2>
        <dl className="grid grid-cols-1 gap-y-1.5 text-sm sm:grid-cols-2">
          <ReadRow label="Brand">{account.brand}</ReadRow>
          <ReadRow label="Terms">{account.terms ?? '--'}</ReadRow>
          <ReadRow label="Salesperson">{account.sage_salesperson_no ?? '--'}</ReadRow>
          <ReadRow label="Open orders">{formatCrmMoney(account.open_order_amount)}</ReadRow>
          <ReadRow label="Weighted pipeline">{formatCrmMoney(account.weighted_pipeline)}</ReadRow>
          <ReadRow label="Open opps">{account.open_opp_count}</ReadRow>
          <ReadRow label="Total orders">{account.order_count}</ReadRow>
          <ReadRow label="Last order">{formatDateShort(account.last_order_date)}</ReadRow>
        </dl>
      </div>
    </div>
  );
}

function OpportunitiesSection({ opps }: { opps: OpportunityRow[] }) {
  return (
    <Section title="Open opportunities" count={opps.length}>
      {opps.length === 0 ? (
        <EmptyRow>No open opportunities.</EmptyRow>
      ) : (
        <table className="min-w-full text-sm">
          <thead className="text-amp-ink-muted dark:text-amp-dark-ink-muted">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Opportunity</th>
              <th className="px-3 py-2 text-left font-medium w-[140px]">Stage</th>
              <th className="px-3 py-2 text-right font-medium w-[120px]">Projected</th>
              <th className="px-3 py-2 text-right font-medium w-[120px]">Weighted</th>
              <th className="px-3 py-2 text-left font-medium w-[110px]">Close</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {opps.map((o) => (
              <tr key={o.id} className="hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm">
                <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">
                  <Link to={`/crm/opportunities/${o.id}`} className="hover:underline">{o.title}</Link>
                  <div className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted font-mono">{o.opp_no}</div>
                </td>
                <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">{o.stage_name ?? '--'}</td>
                <td className="px-3 py-2 text-right text-amp-ink dark:text-amp-dark-ink whitespace-nowrap">{formatCrmMoney(o.projected_amount)}</td>
                <td className="px-3 py-2 text-right text-amp-ink dark:text-amp-dark-ink whitespace-nowrap">{formatCrmMoney(o.weighted_amount)}</td>
                <td className="px-3 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted whitespace-nowrap">{formatDateShort(o.expected_close_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

function OrdersSection({ orders }: { orders: RecentOrderRow[] }) {
  return (
    <Section title="Recent orders" count={orders.length}>
      {orders.length === 0 ? (
        <EmptyRow>No recent orders.</EmptyRow>
      ) : (
        <table className="min-w-full text-sm">
          <thead className="text-amp-ink-muted dark:text-amp-dark-ink-muted">
            <tr>
              <th className="px-3 py-2 text-left font-medium w-[110px]">Order Date</th>
              <th className="px-3 py-2 text-left font-medium">Channel</th>
              <th className="px-3 py-2 text-left font-medium">Sage SO</th>
              <th className="px-3 py-2 text-left font-medium w-[120px]">Status</th>
              <th className="px-3 py-2 text-right font-medium w-[120px]">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {orders.map((o) => (
              <tr key={o.order_id} className="hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm">
                <td className="px-3 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted whitespace-nowrap">{formatDateShort(o.order_date)}</td>
                <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">{o.source_channel}</td>
                <td className="px-3 py-2 font-mono text-xs text-amp-ink dark:text-amp-dark-ink">{o.sage_so_number ?? '--'}</td>
                <td className="px-3 py-2"><Pill tone="bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300">{o.status}</Pill></td>
                <td className="px-3 py-2 text-right text-amp-ink dark:text-amp-dark-ink whitespace-nowrap">{formatCrmMoney(o.total_amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

function ContactsSection({ contacts }: { contacts: ContactRow[] }) {
  return (
    <Section title="Contacts" count={contacts.length}>
      {contacts.length === 0 ? (
        <EmptyRow>No contacts on file.</EmptyRow>
      ) : (
        <table className="min-w-full text-sm">
          <thead className="text-amp-ink-muted dark:text-amp-dark-ink-muted">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Name</th>
              <th className="px-3 py-2 text-left font-medium">Title</th>
              <th className="px-3 py-2 text-left font-medium">Email</th>
              <th className="px-3 py-2 text-left font-medium w-[140px]">Phone</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {contacts.map((c) => (
              <tr key={c.id} className="hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm">
                <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">
                  {c.name}
                  {c.is_primary && <Pill tone="bg-amp-cream-warm text-amp-ink dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink" className="ml-2">Primary</Pill>}
                </td>
                <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">{c.title ?? '--'}</td>
                <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">{c.email ?? '--'}</td>
                <td className="px-3 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted whitespace-nowrap">{c.phone ?? '--'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

function Section({ title, count, children }: { title: string; count: number; children: React.ReactNode }) {
  return (
    <div className="border border-amp-line rounded-lg bg-amp-white overflow-hidden dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <div className="border-b border-amp-line px-3 py-2 dark:border-amp-dark-line">
        <h2 className="text-sm font-semibold text-amp-ink dark:text-amp-dark-ink">
          {title} <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted font-normal">({count})</span>
        </h2>
      </div>
      {children}
    </div>
  );
}

function EmptyRow({ children }: { children: React.ReactNode }) {
  return <p className="px-3 py-4 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">{children}</p>;
}

function ReadRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="w-32 shrink-0 text-amp-ink-muted dark:text-amp-dark-ink-muted">{label}</dt>
      <dd className="flex-1 min-w-0 text-amp-ink dark:text-amp-dark-ink break-words">{children}</dd>
    </div>
  );
}
